/**
 * @voltxampere/spice — Circuit → SPICE text
 *
 * Sprint 16 emit pipeline. Component ordering: sources first,
 * then passive, then active, then connectors. Inside each group
 * we keep the input order (which for imported circuits matches
 * the source file order, so import → export is stable).
 *
 * Net naming flips back through `netToSpiceName` so `gnd → 0`,
 * `n1 → 1`, etc.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { findCodecBySchemaType } from "../element-codecs/index.js";
import { netToSpiceName } from "./net-resolver.js";
/**
 * Type ordering — lines emit in this order so a hand-written
 * netlist reads top-to-bottom from sources down through connectors.
 * Components with types not listed here fall to the bottom in
 * input order.
 */
const TYPE_ORDER = [
    // Sources
    "voltage-source",
    "dc-source",
    "current-source",
    "ac-source",
    "pulse-source",
    // Passive
    "resistor",
    "potentiometer",
    "capacitor",
    "inductor",
    "switch",
    // Active
    "diode",
    "led",
    "zener",
    "bjt",
    "npn",
    "pnp",
    "mosfet",
    "nmos",
    "pmos",
    "jfet",
    "opamp",
    "op-amp",
    // Connectors
    "ground",
];
export function toSpice(circuit, opts = {}) {
    const includeTitle = opts.includeTitle !== false;
    const header = opts.commentHeader ?? "VoltXAmpere v3 export";
    const netById = new Map();
    for (const n of circuit.nets)
        netById.set(n.id, n);
    const sorted = sortComponents(circuit.components);
    const lines = [];
    if (includeTitle) {
        const title = circuit.name.length > 0 ? circuit.name : "Untitled circuit";
        lines.push(`* ${header} · ${title}`);
    }
    for (const comp of sorted) {
        const codec = findCodecBySchemaType(comp.type);
        if (codec === null) {
            lines.push(`* ${comp.id} (${comp.type}) — no codec, skipped`);
            continue;
        }
        const terminalNames = comp.terminals.map((t) => {
            const net = netById.get(t.netId);
            return net ? netToSpiceName(net) : "0";
        });
        lines.push(codec.emit(comp, terminalNames));
    }
    lines.push(".end");
    return lines.join("\n") + "\n";
}
function sortComponents(components) {
    const indexFor = (type) => {
        const idx = TYPE_ORDER.indexOf(type);
        return idx === -1 ? TYPE_ORDER.length : idx;
    };
    // Stable sort: TS sort is stable in V8 ≥ 7.0 (Node 12+).
    return [...components].sort((a, b) => indexFor(a.type) - indexFor(b.type));
}
//# sourceMappingURL=to-spice.js.map